import moment from "moment-timezone";
import { LIMITES, TIPO, VENTA_MESA, TIPO_COMPLEMENTO } from "./schema";
import { aInstante, preguntasAEnviar } from "./payload";

const positivo = (valor) => Number.isInteger(Number(valor)) && Number(valor) > 0;

// finEvento: el fin de la última fecha del evento (la venta no puede seguir después)
// tarifas: los tickets del evento, para controlar la tanda
export const validarTarifa = (form, { finEvento = null, tarifas = [] } = {}) => {
  const errores = {};

  const concepto = form.concepto.trim();
  if (!concepto) {
    errores.concepto = "Ponele un nombre a la tarifa.";
  } else if (concepto.length > LIMITES.CONCEPTO_MAX) {
    errores.concepto = `El nombre puede tener hasta ${LIMITES.CONCEPTO_MAX} caracteres.`;
  }

  if (form.descripcion.trim().length > LIMITES.DESCRIPCION_MAX) {
    errores.descripcion = `La descripción puede tener hasta ${LIMITES.DESCRIPCION_MAX} caracteres.`;
  }

  if (form.precio === "" || Number.isNaN(Number(form.precio)) || Number(form.precio) < 0) {
    errores.precio = "Ingresá un precio (0 si es gratuita).";
  }

  const enTandas = form.idtipo === TIPO.TICKET && !form.privado && form.por_tandas;
  if (!form.privado) {
    // en una tanda sólo se carga el fin: el inicio sale de la tarifa anterior
    if (!form.fin_venta || (!enTandas && !form.inicio_venta)) {
      errores.venta = enTandas ? "Completá hasta cuándo se vende." : "Completá cuándo empieza y termina la venta.";
    } else {
      const fin = aInstante(form.fin_venta);
      if (form.inicio_venta && !aInstante(form.inicio_venta).isBefore(fin)) {
        errores.venta = enTandas
          ? "El fin de la venta tiene que ser posterior al inicio de la tarifa anterior."
          : "El inicio de la venta tiene que ser anterior al fin.";
      } else if (finEvento && fin.isAfter(moment(finEvento))) {
        errores.venta = "La venta no puede terminar después del fin del evento.";
      }
    }
  }

  if (!form.fechas.length) {
    errores.fechas = "Elegí al menos una fecha del evento.";
  }

  const esTicket = form.idtipo === TIPO.TICKET;
  if (esTicket && !form.nueva_ubicacion && !form.idubicacion) {
    errores.idubicacion = "Elegí una ubicación.";
  }
  if (esTicket && form.nueva_ubicacion && !form.nombre_ubicacion.trim()) {
    errores.nombre_ubicacion = "Ponele un nombre a la ubicación.";
  }
  const previa = enTandas && form.idtarifa_previa ? tarifas.find((t) => t.id === Number(form.idtarifa_previa)) : null;
  if (enTandas && !form.idtarifa_previa) {
    errores.idtarifa_previa = "Elegí después de qué tarifa se habilita.";
  } else if (previa && !form.nueva_ubicacion && previa.idubicacion === Number(form.idubicacion)) {
    errores.idtarifa_previa = "Cada tanda necesita su propia ubicación: si comparte la de la anterior, se agotan juntas.";
  }
  // los vouchers tienen límites propios; un ticket, los de la ubicación nueva
  if (!esTicket || form.nueva_ubicacion) {
    if (!positivo(form.limite_persona)) errores.limite_persona = "Tiene que ser un número mayor a 0.";
    if (!positivo(form.limite_maximo)) errores.limite_maximo = "Tiene que ser un número mayor a 0.";
    if (!errores.limite_persona && !errores.limite_maximo && Number(form.limite_persona) > Number(form.limite_maximo)) {
      errores.limite_persona = "No puede superar al límite máximo.";
    }
  }

  if (esTicket && form.venta_mesa === VENTA_MESA.MESA) {
    const error = errorMesa(form);
    if (error) errores.mesa = error;
  }

  if (!positivo(form.unidadstock)) {
    errores.unidadstock = "Tiene que ser un número mayor a 0.";
  }

  if (form.informacion_adicional) {
    const preguntas = preguntasAEnviar(form);
    if (!preguntas.length) {
      errores.preguntas = "Agregá al menos una pregunta.";
    } else if (preguntas.length > LIMITES.PREGUNTAS_MAX) {
      errores.preguntas = `Se pueden agregar hasta ${LIMITES.PREGUNTAS_MAX} preguntas.`;
    } else if (preguntas.some((p) => p.informacion_adicional.length > LIMITES.PREGUNTA_MAX)) {
      errores.preguntas = `Cada pregunta puede tener hasta ${LIMITES.PREGUNTA_MAX} caracteres.`;
    }
  }

  return errores;
};

// errorMesa revisa paquetes y complementos de una tarifa de mesa entera
export const errorMesa = (form) => {
  if (form.paquetes.length > LIMITES.PAQUETES_MAX) return `Se pueden cargar hasta ${LIMITES.PAQUETES_MAX} paquetes.`;
  const personas = new Set();
  for (const p of form.paquetes) {
    const n = Number(p.personas);
    if (!positivo(p.personas) || n > LIMITES.PERSONAS_MESA_MAX) return "Cada paquete necesita una cantidad de personas válida.";
    if (personas.has(n)) return `Hay dos paquetes para ${n} personas.`;
    personas.add(n);
    if (p.precio === "" || Number.isNaN(Number(p.precio)) || Number(p.precio) < 0) return `Completá el precio del paquete de ${n} personas.`;
  }

  const elegidos = form.complementos.filter((c) => c.idtarifa);
  if (elegidos.length > LIMITES.COMPLEMENTOS_MAX) return `Se pueden cargar hasta ${LIMITES.COMPLEMENTOS_MAX} complementos.`;
  const vistos = new Set();
  for (const c of elegidos) {
    if (vistos.has(Number(c.idtarifa))) return "Hay una tarifa repetida entre los complementos.";
    vistos.add(Number(c.idtarifa));
    if (c.tipo === TIPO_COMPLEMENTO.ADICIONAL && !positivo(c.maximo_por_mesa)) {
      return "Cada adicional necesita un máximo por mesa.";
    }
  }
  return null;
};

export const hayErrores = (errores) => Object.keys(errores).length > 0;
